import {
    ORDER_GRID_TYPE,
    FIXTURE_GRID_TYPE,
} from '_legacy/constants/gridTypes';
import {
    getDefaultOrdersRowGroupsSorting,
    getDefaultFixturesRowGroupsSorting,
} from '../services/RowGroupsSortingService';
import {
    getDefaultOrdersRowGroups,
    getDefaultFixturesRowGroups,
} from '../services/RowGroupsService';
import layoutsInitialState from 'store/feature/layoutsSlice/initial';
import {
    fetchShowDeletedFixtures,
    fetchShowDeletedOrders,
} from '_legacy/localStorage';

const ZONE_FILTER_RENAMES = {
    loadZone: 'loadZones',
    dischargeZone: 'dischargeZones',
    deliveryZone: 'deliveryZones',
};

const migrateFilterKeys = (filters) => {
    if (!filters || typeof filters !== 'object' || Array.isArray(filters))
        return filters;
    const hasOldKey = Object.keys(ZONE_FILTER_RENAMES).some(
        (k) => filters[k] !== undefined
    );
    if (!hasOldKey) return filters;
    const migrated = { ...filters };
    for (const oldKey of Object.keys(ZONE_FILTER_RENAMES)) {
        if (migrated[oldKey] !== undefined) {
            const newKey = ZONE_FILTER_RENAMES[oldKey];
            if (migrated[newKey] === undefined) {
                migrated[newKey] = migrated[oldKey];
            }
            delete migrated[oldKey];
        }
    }
    return migrated;
};

export const getSelectedLayoutOptions = (selectedLayout, datasetId) => {
    if (!selectedLayout) return layoutsInitialState.selectedLayout;

    const orders =
        selectedLayout[ORDER_GRID_TYPE] &&
        JSON.parse(selectedLayout[ORDER_GRID_TYPE]);
    const fixtures =
        selectedLayout[FIXTURE_GRID_TYPE] &&
        JSON.parse(selectedLayout[FIXTURE_GRID_TYPE]);
    const commonSettings = getCommonSettings(selectedLayout.commonSettings);

    return {
        selectedLayoutId: selectedLayout.id,
        areThereAnyUnsavedLayoutChanges: false,
        commonSettings: commonSettings,
        fixtures: !fixtures
            ? layoutsInitialState.selectedLayout.fixtures
            : {
                  initialOptions: getFixturesOptions(
                      fixtures,
                      datasetId,
                      commonSettings.directionLogic.currentState
                  ),
                  currentOptions: getFixturesOptions(
                      fixtures,
                      datasetId,
                      commonSettings.directionLogic.currentState
                  ),
                  collapsedRowGroups:
                      fixtures.collapsedRowGroups ||
                      layoutsInitialState.selectedLayout.fixtures
                          .collapsedRowGroups,
                  hasRecentChanges: false,
                  hasDifferencies: {},
                  hasChanges: false,
              },
        orders: !orders
            ? layoutsInitialState.selectedLayout.orders
            : {
                  initialOptions: getOrdersOptions(
                      orders,
                      datasetId,
                      commonSettings.directionLogic.currentState
                  ),
                  currentOptions: getOrdersOptions(
                      orders,
                      datasetId,
                      commonSettings.directionLogic.currentState
                  ),
                  collapsedRowGroups:
                      orders.collapsedRowGroups ||
                      layoutsInitialState.selectedLayout.orders
                          .collapsedRowGroups,
                  hasRecentChanges: false,
                  hasDifferencies: {},
                  hasChanges: false,
              },
    };
};

const getOrdersOptions = (orders, datasetId, directionLogic) => {
    const { initialOptions } = layoutsInitialState.selectedLayout.orders;

    return {
        filterOptions: migrateFilterKeys(orders.filters),
        columnOptions: orders.columnState,
        shouldHighlightNewOrders:
            orders.shouldHighlightNewOrders ||
            initialOptions.shouldHighlightNewOrders,
        shouldShowConvertedOrders:
            orders.shouldShowConvertedOrders ||
            initialOptions.shouldShowConvertedOrders,
        shouldOnlyDisplayRumouredOrders:
            orders.shouldOnlyDisplayRumouredOrders ||
            initialOptions.shouldOnlyDisplayRumouredOrders,
        shouldShowDeletedOrders:
            (orders.shouldShowDeletedOrders === undefined
                ? fetchShowDeletedOrders()
                : orders.shouldShowDeletedOrders) ||
            initialOptions.shouldShowDeletedOrders,
        rowGroups: getCurrentOrDefaultValue(
            orders.rowGroups,
            getDefaultOrdersRowGroups(datasetId)
        ),
        rowGroupsSorting: getCurrentOrDefaultValue(
            orders.rowGroupsSorting,
            getDefaultOrdersRowGroupsSorting(datasetId, directionLogic)
        ),
    };
};

const getFixturesOptions = (fixtures, datasetId, directionLogic) => {
    const { initialOptions } = layoutsInitialState.selectedLayout.fixtures;

    return {
        filterOptions: migrateFilterKeys(fixtures.filters),
        columnOptions: fixtures.columnState,
        vesselOptions: fixtures.vesselOptions || initialOptions.vesselOptions,
        shouldOnlyDisplayRumouredFixtures:
            fixtures.shouldOnlyDisplayRumouredFixtures ||
            initialOptions.shouldOnlyDisplayRumouredFixtures,
        shouldShowDeletedFixtures:
            (fixtures.shouldShowDeletedFixtures === undefined
                ? fetchShowDeletedFixtures()
                : fixtures.shouldShowDeletedFixtures) ||
            initialOptions.shouldShowDeletedFixtures,
        rowGroups: getCurrentOrDefaultValue(
            fixtures.rowGroups,
            getDefaultFixturesRowGroups(datasetId)
        ),
        rowGroupsSorting: getCurrentOrDefaultValue(
            fixtures.rowGroupsSorting,
            getDefaultFixturesRowGroupsSorting(datasetId, directionLogic)
        ),
    };
};

const getCommonSettings = (commonSettings) => {
    const { selectedLayout } = layoutsInitialState;
    return !commonSettings
        ? selectedLayout.commonSettings
        : {
              directionLogic: {
                  initialState: commonSettings.directionLogic,
                  currentState: commonSettings.directionLogic,
              },
              quantityFormat: {
                  initialState: commonSettings.quantityFormat,
                  currentState: commonSettings.quantityFormat,
              },
              rateFormat: {
                  initialState:
                      commonSettings.rateFormat ??
                      selectedLayout.commonSettings.rateFormat.initialState,
                  currentState:
                      commonSettings.rateFormat ??
                      selectedLayout.commonSettings.rateFormat.currentState,
              },
              defaultType: {
                  initialState: commonSettings.defaultType,
                  currentState: commonSettings.defaultType,
              },
          };
};

const getCurrentOrDefaultValue = (currentValue, defaultValue) => {
    if (!currentValue || currentValue.length === 0) {
        return defaultValue;
    }
    return currentValue;
};
